import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '../services/api';

/**
 * Deep-link gate for https://sulhafa.com/profile/{id} (mobile App Links /
 * Universal Links). When the app is installed the OS intercepts the link and
 * this page never renders; it only loads when the app is absent — so:
 *
 *   - Android  -> Google Play listing
 *   - iOS      -> App Store listing
 *   - crawler  -> the landing page (no redirect; best-effort OG tags)
 *   - desktop  -> the landing page with store buttons + "view on web"
 *
 * Set VITE_APP_STORE_ID in .env to the numeric App Store id (from the
 * apps.apple.com/.../idXXXXXXXXXX link). Falls back to a plain App Store
 * search until then.
 */
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.sulhafa.sulhafaApp';
const APP_STORE_ID = import.meta.env.VITE_APP_STORE_ID;
const APP_STORE_URL = APP_STORE_ID
    ? `https://apps.apple.com/app/id${APP_STORE_ID}`
    : 'https://apps.apple.com/us/search?sulhafa';

const ANDROID_RE = /android/i;
const IOS_RE = /iphone|ipad|ipod/i;
// Crawlers must never be redirected — they need the page for link previews.
const BOT_RE = /whatsapp|facebookexternalhit|twitterbot|linkedinbot|slackbot|telegrambot|discordbot|googlebot|bingbot|applebot|crawler|spider|preview/i;

const ProfileLinkGate = () => {
    const { profileId } = useParams();
    const ua = navigator.userAgent || '';
    const isBot = BOT_RE.test(ua);
    const isAndroid = ANDROID_RE.test(ua);
    const isIos = IOS_RE.test(ua);

    const [profile, setProfile] = useState(null);

    useEffect(() => {
        if (isAndroid && !isBot) {
            window.location.replace(PLAY_STORE_URL);
            return;
        }
        if (isIos && !isBot) {
            window.location.replace(APP_STORE_URL);
            return;
        }
        // Landing view (desktop + crawlers): best-effort profile lookup for
        // the preview. Public visitors may not be authed — degrade silently.
        if (profileId) {
            api.get(`/api/v1/user-profile/${profileId}`)
                .then(res => {
                    const data = res?.data?.data || res?.data || {};
                    setProfile({
                        name: data.name || [data.first_name, data.last_name].filter(Boolean).join(' '),
                        photo: data.profile_image_url || data.profile_image || null,
                    });
                })
                .catch(() => setProfile({}));
        }
        document.title = profile?.name
            ? `${profile.name} — Sulhafa`
            : 'Sulhafa profile';
        // Best-effort Open Graph injection (works for scrapers that run JS;
        // server-rendered OG needs the backend route — see runbook note).
        const setMeta = (prop, content) => {
            if (!content) return;
            let tag = document.querySelector(`meta[property="${prop}"]`);
            if (!tag) {
                tag = document.createElement('meta');
                tag.setAttribute('property', prop);
                document.head.appendChild(tag);
            }
            tag.setAttribute('content', content);
        };
        setMeta('og:title', profile?.name ? `${profile.name} on Sulhafa` : 'Sulhafa');
        setMeta('og:description', 'Follow me on Sulhafa — the pet care community.');
        setMeta('og:type', 'profile');
    }, [profileId, isAndroid, isIos, isBot]);

    // Phone users mid-redirect: show nothing but a tiny spinner frame.
    if ((isAndroid || isIos) && !isBot) {
        return (
            <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6B7280', fontFamily: '"Red Hat Display", sans-serif' }}>
                Opening store…
            </div>
        );
    }

    // Landing view: desktop + crawlers.
    return (
        <div style={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#FAFAFA',
            padding: '24px',
            fontFamily: '"Red Hat Display", sans-serif',
        }}>
            <div style={{
                background: '#fff',
                border: '1px solid #E5E7EB',
                borderRadius: '16px',
                boxShadow: '0 10px 25px -5px rgba(0,0,0,0.08)',
                padding: '40px',
                maxWidth: '380px',
                width: '100%',
                textAlign: 'center',
            }}>
                <img
                    src="/sulhafa-favicon.png"
                    alt="Sulhafa"
                    style={{ width: '56px', height: '56px', borderRadius: '14px', marginBottom: '16px' }}
                />
                {profile?.photo ? (
                    <img
                        src={profile.photo}
                        alt=""
                        style={{ width: '80px', height: '80px', borderRadius: '50%', objectFit: 'cover', margin: '0 auto 12px', display: 'block', border: '3px solid #00D4B3' }}
                    />
                ) : null}
                <h1 style={{ fontSize: '20px', fontWeight: 800, color: '#1F2937', margin: '0 0 6px' }}>
                    {profile?.name ? `${profile.name}` : 'Sulhafa profile'}
                </h1>
                <p style={{ fontSize: '14px', color: '#6B7280', margin: '0 0 24px' }}>
                    {profile?.name
                        ? `See ${profile.name}'s pets, posts and more on Sulhafa.`
                        : 'Pets, vets and a community that cares — all in one app.'}
                </p>

                <a href={PLAY_STORE_URL} target="_blank" rel="noreferrer" style={storeButton('#1F2937')}>
                    Get it on Google Play
                </a>
                <a href={APP_STORE_URL} target="_blank" rel="noreferrer" style={storeButton('#1F2937')}>
                    Download on the App Store
                </a>
                <a href={`/social/profile/${profileId}`} style={{ ...storeButton('#00D4B3'), marginTop: '12px' }}>
                    Continue on the web
                </a>
            </div>
        </div>
    );
};

const storeButton = (bg) => ({
    display: 'block',
    background: bg,
    color: '#fff',
    textDecoration: 'none',
    fontWeight: 700,
    fontSize: '14px',
    padding: '12px 16px',
    borderRadius: '999px',
    marginBottom: '10px',
    textAlign: 'center',
});

export default ProfileLinkGate;
